import { useState, useCallback } from 'react';

export const useTalukaSelection = (tahsils, scenario) => {
  const [selectedTalukaIds, setSelectedTalukaIds] = useState([]);
  const [lastSelectedId, setLastSelectedId] = useState(null);

  const talukasWithData = tahsils.filter(t => t.hasData === true);
  const talukasWithoutData = tahsils.filter(t => t.hasData !== true);

  const submittedTalukas = talukasWithData.filter(t => t.status === 'submitted' || t.status?.toLowerCase() === 'pending');

  const handleTalukaClick = useCallback((talukaId, event) => {
    const isCtrlKey = event?.ctrlKey || event?.metaKey;
    const isShiftKey = event?.shiftKey;

    setSelectedTalukaIds(prev => {
      let newIds;

      if (isCtrlKey) {
        if (prev.includes(talukaId)) {
          newIds = prev.filter(id => id !== talukaId);
        } else {
          newIds = [...prev, talukaId];
        }
      } 
      else if (isShiftKey && lastSelectedId && prev.length > 0) {
        const allTalukaIds = tahsils.map(t => t._id);
        const lastIndex = allTalukaIds.indexOf(lastSelectedId);
        const currentIndex = allTalukaIds.indexOf(talukaId);
        
        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          newIds = allTalukaIds.slice(start, end + 1);
        } else {
          newIds = [talukaId];
        }
      } 
      else {
        newIds = [talukaId];
      }

      setLastSelectedId(talukaId);
      return newIds;
    });
  }, [tahsils, lastSelectedId]);

  const handleTalukaCheck = useCallback((talukaId, checked, event) => {
    event?.stopPropagation();
    
    setSelectedTalukaIds(prev => {
      let newIds;
      if (checked) {
        newIds = [...prev, talukaId];
      } else {
        newIds = prev.filter(id => id !== talukaId);
      }

      setLastSelectedId(talukaId);
      return newIds;
    });
  }, []);

  // Updated function to only select submitted talukas
  const selectAllSubmitted = useCallback((checked) => {
    if (checked) {
      // Select all submitted talukas
      const ids = submittedTalukas.map(t => t._id);
      setSelectedTalukaIds(ids);
      setLastSelectedId(ids[ids.length - 1] || null);
    } else {
      // Deselect all submitted talukas (keep other selected talukas if any)
      setSelectedTalukaIds(prev => 
        prev.filter(id => !submittedTalukas.some(t => t._id === id))
      );
      // Update lastSelectedId if needed
      if (submittedTalukas.some(t => t._id === lastSelectedId)) {
        setLastSelectedId(null);
      }
    }
  }, [submittedTalukas]);

  // Original selectAllWithData function (if you still need it)
  const selectAllWithData = useCallback((checked) => {
    if (checked) {
      const ids = talukasWithData.map(t => t._id);
      setSelectedTalukaIds(ids);
      setLastSelectedId(ids[ids.length - 1] || null);
    } else {
      setSelectedTalukaIds(prev => 
        prev.filter(id => !talukasWithData.some(t => t._id === id))
      );
      if (talukasWithData.some(t => t._id === lastSelectedId)) {
        setLastSelectedId(null);
      }
    }
  }, [talukasWithData]);

  const clearSelection = useCallback(() => {
    setSelectedTalukaIds([]);
    setLastSelectedId(null);
  }, []);

  const isSelected = useCallback((talukaId) => {
    return selectedTalukaIds.includes(talukaId);
  }, [selectedTalukaIds]);

  const getSelectedCount = useCallback(() => {
    return selectedTalukaIds.length;
  }, [selectedTalukaIds]);

  // Helper function to check if all submitted are selected
  const areAllSubmittedSelected = useCallback(() => {
    return submittedTalukas.length > 0 && 
           submittedTalukas.every(t => selectedTalukaIds.includes(t._id));
  }, [submittedTalukas, selectedTalukaIds]);

  return {
    selectedTalukaIds,
    talukasWithData,
    talukasWithoutData,
    submittedTalukas, // Export this for use in TalukaPanel
    handleTalukaClick,
    handleTalukaCheck,
    selectAllWithData,
    selectAllSubmitted, // New function for selecting only submitted
    clearSelection,
    isSelected,
    getSelectedCount,
    areAllSubmittedSelected // Helper to check if all submitted are selected
  };
};